import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Drawer, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Close, FavoriteBorder, Home, Logout, Menu, PersonOutline, Search, Settings, VerifiedUser, Visibility, VisibilityOff, Wallet } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import HeaderProfileMenu from '../components/HeaderProfileMenu';
import '../styles/maandvo-dashboard.css';

const MAROON = '#7b0011';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) return toast.error('Please complete all fields');
    if (newPassword.length < 8) return toast.error('New password must be at least 8 characters long');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    if (newPassword === currentPassword) return toast.error('New password must be different from current password');
    try {
      setSubmitting(true);
      await api.post('/auth/change-password', { email: user?.email, current_password: currentPassword, new_password: newPassword });
      toast.success('Password updated successfully');
      navigate('/my-profile');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Unable to update password');
    } finally {
      setSubmitting(false);
    }
  };

  const passwordField = (label: string, value: string, setValue: (nextValue: string) => void, visible: boolean, setVisible: (nextValue: boolean) => void) => (
    <TextField fullWidth label={label} value={value} onChange={(event) => setValue(event.target.value)} type={visible ? 'text' : 'password'} margin="normal" InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setVisible(!visible)} edge="end" sx={{ color: MAROON }}>{visible ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
  );

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const sidebarItems = useMemo(() => [
    { label: 'Dashboard', icon: <Home fontSize="small" />, action: () => navigate('/my-profile') },
    { label: 'Update Profile', icon: <PersonOutline fontSize="small" />, action: () => navigate('/my-profile?openCompleteProfile=1') },
    { label: 'Match Preferences', icon: <Settings fontSize="small" />, action: () => navigate('/my-profile?openMatchPreferences=1') },
    { label: 'Search Profiles', icon: <Home fontSize="small" />, action: () => navigate('/') },
    { label: 'Unlocked Profiles', icon: <FavoriteBorder fontSize="small" />, action: () => navigate('/my-connections') },
    { label: 'Recharge Account', icon: <Wallet fontSize="small" />, action: () => navigate('/wallet') },
    { label: 'Activation Status', icon: <VerifiedUser fontSize="small" />, action: () => navigate('/activation-status') },
    { label: 'Change Password', icon: <Settings fontSize="small" />, action: () => navigate('/change-password'), active: true },
    { label: 'Logout', icon: <Logout fontSize="small" />, action: handleLogout },
  ], [navigate]);

  const sidebarContent = (
    <Box className="maandvo-dashboard-sidebar" role="presentation">
      <Box className="maandvo-dashboard-brand"><Box component="img" src="/maandvo-logo.png" alt="Brand" /></Box>
      <Box className="maandvo-dashboard-nav">{sidebarItems.map((item) => <Box key={item.label} className={`maandvo-dashboard-nav-item ${item.active ? 'active' : ''}`} onClick={item.action}>{item.icon}<span>{item.label}</span></Box>)}</Box>
    </Box>
  );

  return (
    <Box className="maandvo-dashboard">
      <Box className="maandvo-dashboard-mobile-top"><IconButton onClick={() => setMobileOpen(true)} sx={{ color: 'inherit' }}><Menu /></IconButton><Typography sx={{ fontWeight: 800 }}>Change Password</Typography><HeaderProfileMenu initial={user?.first_name?.[0] || 'U'} actions={[{ label: 'Dashboard', onClick: () => navigate('/my-profile') }, { label: 'Logout', onClick: handleLogout, icon: <Logout fontSize="small" />, color: '#b71c1c' }]} /></Box>
      <Box className="maandvo-dashboard-layout">
        <Box className="maandvo-dashboard-sidebar desktop-only">{sidebarContent}</Box>
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { xs: 'block', md: 'none' } }}><Box sx={{ width: 280 }}><Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}><IconButton onClick={() => setMobileOpen(false)}><Close /></IconButton></Box>{sidebarContent}</Box></Drawer>
        <Box className="maandvo-dashboard-main">
          <Box sx={{ maxWidth: 640, mx: 'auto', py: { xs: 2, md: 5 } }}>
            <Card className="maandvo-dashboard-panel" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                <Typography variant="h4" sx={{ color: MAROON, fontWeight: 800 }}>Change Password</Typography>
                <Typography sx={{ color: '#75685f', mt: 1 }}>Enter your current password to choose a new password.</Typography>
                <Alert severity="info" sx={{ mt: 2 }}>Forgot your current password? Use the Forgot Password link on the login page.</Alert>
                <Box component="form" onSubmit={submit} sx={{ mt: 1 }}>
                  <TextField fullWidth label="Email" value={user?.email || ''} margin="normal" InputProps={{ readOnly: true }} />
                  {passwordField('Current Password', currentPassword, setCurrentPassword, showCurrent, setShowCurrent)}
                  {passwordField('New Password', newPassword, setNewPassword, showNew, setShowNew)}
                  {passwordField('Confirm New Password', confirmPassword, setConfirmPassword, showConfirm, setShowConfirm)}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
                    <Button onClick={() => navigate('/my-profile')} sx={{ color: MAROON }}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={submitting} sx={{ backgroundColor: MAROON }}>{submitting ? <CircularProgress size={22} color="inherit" /> : 'Update Password'}</Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChangePasswordPage;